import styled from '@emotion/styled';
import { ob } from '../../onboardingV2.tokens';

/** Live preview of the storefront (caption + phone). Placement/visibility is set by the layout. */
export const Root = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    width: 100%;
    /* The preview is for looking, not interacting: no selection, no cursor, no clicks. */
    pointer-events: none;
    user-select: none;
    -webkit-user-select: none;
    cursor: default;
`;

export const Caption = styled.p`
    margin: 0;
    font-family: ${ob.font.heading};
    font-size: 15px;
    font-weight: 700;
    color: ${ob.color.brand};
`;

export const Phone = styled.div`
    width: 320px;
    max-width: 100%;
    border-radius: 40px;
    padding: 10px;
    background: #10231a;
    box-shadow: ${ob.shadow.raised};
`;

export const Screen = styled.div`
    position: relative;
    height: 580px;
    overflow: hidden;
    border-radius: 30px;
    background: ${ob.color.surface};
    display: flex;
    flex-direction: column;
`;

export const UrlBar = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 11px 14px 9px;
    font-size: 11px;
    color: ${ob.color.muted};
    border-bottom: 1px solid ${ob.color.borderSubtle};

    > span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    > i {
        flex-shrink: 0;
        font-size: 13px;
    }
`;

export const Scroll = styled.div`
    flex: 1;
    overflow: hidden;
`;

export const Banner = styled.div`
    height: 84px;
    background:
        ${(props) => (props.src ? `center / cover no-repeat url(${props.src}),` : '')}
        linear-gradient(120deg, #efe9df 0%, #e7e0d4 50%, #dde8d2 100%);
    flex-shrink: 0;
`;

export const Head = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 0 14px;
    margin-top: -22px;
`;

export const Avatar = styled.span`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    border-radius: 50%;
    border: 3px solid ${ob.color.surface};
    background:
        ${(props) => (props.src ? `center / cover no-repeat url(${props.src}),` : '')}
        ${ob.color.lime100};
    color: ${ob.color.brand};
    font-family: ${ob.font.heading};
    font-size: 20px;
    font-weight: 700;
    text-transform: uppercase;
    box-shadow: 0 2px 8px rgba(16, 35, 26, 0.12);
`;

export const NameBlock = styled.div`
    min-width: 0;
    padding-top: 26px;
`;

export const Name = styled.strong`
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: ${ob.font.heading};
    font-size: 17px;
    font-weight: 700;
    color: ${ob.color.brand};
`;

export const Status = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin-top: 2px;
    font-size: 11px;
    color: ${ob.color.muted};

    &::before {
        content: '';
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: ${ob.color.whatsapp};
    }
`;

export const Desc = styled.p`
    margin: 8px 14px 0;
    font-size: 11.5px;
    line-height: 1.45;
    color: ${ob.color.body};
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

export const Cats = styled.div`
    display: flex;
    gap: 12px;
    padding: 14px 14px 6px;
    overflow: hidden;
`;

export const Cat = styled.div`
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    width: 52px;

    .circle {
        width: 50px;
        height: 50px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        background: ${(props) => (props.active ? ob.color.brand : ob.color.subtle)};
        color: ${(props) => (props.active ? ob.color.surface : ob.color.body)};
        border: 1px solid ${(props) => (props.active ? ob.color.brand : ob.color.borderSubtle)};
    }

    .label {
        max-width: 52px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 10.5px;
        font-weight: ${(props) => (props.active ? 700 : 500)};
        color: ${(props) => (props.active ? ob.color.brand : ob.color.body)};
    }
`;

/** Subcategory pills of the open category (storefront SubcategoryScrollRow). */
export const SubPills = styled.div`
    display: flex;
    gap: 6px;
    padding: 4px 14px 6px;
    overflow: hidden;

    .pill {
        flex-shrink: 0;
        padding: 4px 10px;
        border-radius: ${ob.radius.pill};
        border: 1px solid ${ob.color.borderSubtle};
        background: ${ob.color.surface};
        font-size: 10.5px;
        font-weight: 500;
        color: ${ob.color.body};
        white-space: nowrap;
    }

    .pill.active {
        border-color: ${ob.color.brand};
        background: ${ob.color.brand};
        color: ${ob.color.surface};
        font-weight: 700;
    }
`;

export const Grid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    padding: 12px 14px 16px;
`;

export const Cell = styled.div`
    display: flex;
    flex-direction: column;
`;

export const Thumb = styled.div`
    position: relative;
    height: 104px;
    border-radius: ${ob.radius.md};
    background:
        ${(props) => (props.src ? `center / cover no-repeat url(${props.src}),` : '')}
        ${ob.color.subtle};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${ob.color.muted};
    font-size: 24px;
`;

export const Plus = styled.span`
    position: absolute;
    top: 6px;
    right: 6px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: ${ob.color.surface};
    color: ${ob.color.brand};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    box-shadow: 0 1px 4px rgba(16, 35, 26, 0.18);
`;

export const Badge = styled.span`
    position: absolute;
    top: 6px;
    left: 6px;
    padding: 2px 7px;
    border-radius: ${ob.radius.pill};
    background: ${ob.color.brand};
    color: ${ob.color.surface};
    font-size: 10px;
    font-weight: 700;
`;

export const Info = styled.div`
    padding: 6px 2px 0;

    .pname {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 12px;
        color: ${ob.color.text};
    }

    .price {
        margin-top: 1px;
        font-size: 12.5px;
        font-weight: 700;
        color: ${ob.color.text};
    }

    .tier {
        margin-top: 2px;
        font-size: 10px;
        font-weight: 600;
        color: ${ob.color.lime700};
    }
`;

export const Detail = styled.span`
    display: block;
    margin-top: 7px;
    padding: 6px 0;
    border-radius: ${ob.radius.pill};
    border: 1px solid ${ob.color.border};
    text-align: center;
    font-size: 11px;
    font-weight: 600;
    color: ${ob.color.brand};
`;

export const GhostLine = styled.div`
    height: ${(props) => props.h || '10px'};
    width: ${(props) => props.w || '100%'};
    border-radius: 6px;
    background: ${ob.color.skeleton};
    margin-top: ${(props) => props.mt || '6px'};
`;
